import { autoUpdate, flip, FloatingPortal, offset, useFloating } from "@floating-ui/react";
import { Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";

import { cn } from "@/components/ui/lib/utils";

import { SlashMenuDescription } from "./slash-menu-description";
import type { SlashMenuItem as SlashMenuItemData } from "./slash-menu-types";

export type SlashMenuItemProps = {
	item: SlashMenuItemData;
	selected: boolean;
	onPick: () => void;
	onHover: () => void;
};

export function SlashMenuItem({ item, selected, onPick, onHover }: SlashMenuItemProps) {
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	const tooltip = useFloating({
		placement: "right-start",
		strategy: "fixed",
		middleware: [offset(8), flip({ fallbackPlacements: ["left-start"], padding: 8 })],
		whileElementsMounted: autoUpdate,
	});

	useEffect(() => {
		if (selected) buttonRef.current?.scrollIntoView({ block: "nearest" });
	}, [selected]);

	const showTooltip = selected && !!item.description;

	return (
		<>
			<button
				ref={(el) => {
					buttonRef.current = el;
					tooltip.refs.setReference(el);
				}}
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					onPick();
				}}
				onMouseEnter={onHover}
				className={cn(
					"group/dropdown-menu-item relative flex w-full cursor-default items-start gap-2 rounded-md px-1.5 py-1 text-left text-sm outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
					selected && "bg-accent text-accent-foreground",
				)}
			>
				<span className="flex size-5 shrink-0 items-center justify-center text-base">
					{item.emoji ?? <Sparkles className="size-3.5 text-muted-foreground" />}
				</span>
				<span className="min-w-0 flex-1">
					<span className="block truncate font-medium leading-tight">{item.name}</span>
				</span>
			</button>
			{showTooltip && (
				<FloatingPortal>
					<SlashMenuDescription
						ref={tooltip.refs.setFloating}
						description={item.description as string}
						style={{
							...tooltip.floatingStyles,
							visibility: tooltip.isPositioned ? "visible" : "hidden",
						}}
					/>
				</FloatingPortal>
			)}
		</>
	);
}
